"use client";
// LEGACY UI: replace with Select, or DropdownMenu with checkbox items for multi-select from @workspace/frontend-ui.
// Don't import this in new code; legacy-ui.json tracks the remaining importers
// and only shrinks (scripts/legacy-ui-check.mjs).

import classNames from "@casino/external/classNames";
import ControlsChevronDown from "@casino/external/icons/ControlsChevronDown";
import { createContext, useContext, useEffect, useRef, useState } from "react";

type DropdownContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: unknown;
  onChange: (optionValue: unknown) => void;
  multiple: boolean;
  size: string;
  disabled: boolean;
  isError: boolean;
};

const DropdownCtx = createContext<DropdownContextValue>({
  open: false,
  setOpen: () => {},
  value: undefined,
  onChange: () => {},
  multiple: false,
  size: "md",
  disabled: false,
  isError: false,
});

type ActiveContextValue = {
  active: unknown;
  setActive: (value: unknown) => void;
};

const ActiveCtx = createContext<ActiveContextValue>({
  active: undefined,
  setActive: () => {},
});

const SIZE_CLASSES: Record<string, string> = {
  "2xs": "h-6 py-0.5 px-2 text-xs",
  xs: "h-8 py-1 px-3 text-sm/6",
  sm: "h-8 py-1 px-3 text-sm/6",
  md: "h-10 py-2 px-3 text-sm/6",
  lg: "h-12 py-3 px-4 text-sm/6",
  xl: "h-14 py-4 px-4 text-base",
};

const getDataProps = (rest: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(rest).filter(
      ([k]) => k.startsWith("data-") || k.startsWith("aria-"),
    ),
  );

const isValueSelected = (
  value: unknown,
  optionValue: unknown,
  multiple: boolean,
): boolean => {
  if (multiple && Array.isArray(value)) return value.includes(optionValue);
  return value === optionValue;
};

type RootProps = {
  children:
    | React.ReactNode
    | (({ open }: { open: boolean }) => React.ReactNode);
  value?: unknown;
  onChange?: (value: unknown) => void;
  multiple?: boolean;
  size?: string;
  className?: string;
  disabled?: boolean;
  isError?: boolean;
  onClear?: () => void;
  position?: string;
  [key: string]: unknown;
};

const DropdownRoot = ({
  children,
  value,
  onChange,
  multiple = false,
  size = "md",
  className,
  disabled = false,
  isError = false,
  ...rest
}: RootProps) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<unknown>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const handleChange = (optionValue: unknown) => {
    if (multiple && Array.isArray(value)) {
      const next = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange?.(next);
    } else {
      onChange?.(optionValue);
      setOpen(false);
    }
  };

  return (
    <DropdownCtx.Provider
      value={{
        open,
        setOpen,
        value,
        onChange: handleChange,
        multiple,
        size,
        disabled,
        isError,
      }}
    >
      <ActiveCtx.Provider value={{ active, setActive }}>
        <div
          ref={containerRef}
          className={classNames("w-full relative", className)}
          {...getDataProps(rest)}
        >
          {typeof children === "function" ? children({ open }) : children}
        </div>
      </ActiveCtx.Provider>
    </DropdownCtx.Provider>
  );
};

type TriggerProps = { children: React.ReactNode; className?: string };

const Trigger = ({ children, className }: TriggerProps) => {
  const { setOpen, open, disabled } = useContext(DropdownCtx);
  return (
    <button
      type="button"
      onClick={() => !disabled && setOpen(!open)}
      className={classNames(
        "cursor-pointer",
        disabled && "opacity-60 cursor-not-allowed",
        className,
      )}
    >
      {children}
    </button>
  );
};

type OptionsProps = {
  children: React.ReactNode;
  className?: string;
  menuWidth?: string;
};

const Options = ({ children, className, menuWidth }: OptionsProps) => {
  const { open } = useContext(DropdownCtx);
  if (!open) return null;
  return (
    <div
      role="listbox"
      className={classNames(
        menuWidth || "w-full min-w-[18.75rem]",
        "z-5 absolute p-1 my-2 rounded-xl box-border bg-background shadow-lg overflow-y-auto focus:outline-none",
        className,
      )}
    >
      {children}
    </div>
  );
};

type OptionProps = {
  value: unknown;
  children:
    | React.ReactNode
    | (({
        selected,
        active,
      }: {
        selected: boolean;
        active: boolean;
      }) => React.ReactNode);
};

const Option = ({ value: optionValue, children }: OptionProps) => {
  const { value, onChange, multiple } = useContext(DropdownCtx);
  const { active, setActive } = useContext(ActiveCtx);
  const selected = isValueSelected(value, optionValue, multiple);
  const isActive = active === optionValue;

  return (
    <div
      role="option"
      aria-selected={selected}
      onClick={() => onChange(optionValue)}
      onMouseEnter={() => setActive(optionValue)}
      onMouseLeave={() => setActive(undefined)}
      className="cursor-pointer"
    >
      {typeof children === "function"
        ? children({ selected, active: isActive })
        : children}
    </div>
  );
};

type MultiSelectProps = {
  open: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  className?: string;
  [key: string]: unknown;
};

const MultiSelect = ({
  open,
  label,
  placeholder,
  className,
  ...rest
}: MultiSelectProps) => {
  const {
    open: ctxOpen,
    setOpen,
    size,
    disabled,
    isError,
  } = useContext(DropdownCtx);
  const isOpen = open ?? ctxOpen;
  return (
    <div>
      {label && (
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
      )}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!ctxOpen)}
        className={classNames(
          "flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-background inset-ring inset-ring-border hover:inset-ring-2 hover:inset-ring-border transition-shadow duration-200 focus:inset-ring-2 focus:inset-ring-primary focus:outline-none",
          SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
          isError &&
            "inset-ring-2 inset-ring-destructive hover:inset-ring-2 hover:inset-ring-destructive focus:inset-ring-2 focus:inset-ring-destructive",
          disabled &&
            "opacity-60 cursor-not-allowed hover:inset-ring hover:inset-ring-border",
          className,
        )}
        {...getDataProps(rest)}
      >
        <span className="flex w-full flex-col items-start text-start overflow-hidden">
          <span
            className={classNames(
              size === "sm" ? "text-sm/6" : "text-base",
              "w-full text-muted-foreground text-start truncate",
            )}
          >
            {placeholder}
          </span>
        </span>
        <ControlsChevronDown
          className={classNames(
            "text-muted-foreground flex-shrink-0 transition-transform",
            size === "sm" ? "text-base" : "text-2xl",
            isOpen && "rotate-180",
          )}
        />
      </button>
    </div>
  );
};

type SelectProps = {
  open: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  children?: React.ReactNode;
  className?: string;
  [key: string]: unknown;
};

const Select = ({
  open,
  label,
  placeholder,
  children,
  className,
  ...rest
}: SelectProps) => {
  const {
    open: ctxOpen,
    setOpen,
    size,
    disabled,
    isError,
  } = useContext(DropdownCtx);
  const isOpen = open ?? ctxOpen;
  return (
    <div>
      {label && (
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
      )}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!ctxOpen)}
        className={classNames(
          "flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-background inset-ring inset-ring-border hover:inset-ring-2 hover:inset-ring-border transition-shadow duration-200 focus:inset-ring-2 focus:inset-ring-primary focus:outline-none",
          SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
          isError &&
            "inset-ring-2 inset-ring-destructive hover:inset-ring-2 hover:inset-ring-destructive focus:inset-ring-2 focus:inset-ring-destructive",
          disabled &&
            "opacity-60 cursor-not-allowed hover:inset-ring hover:inset-ring-border",
          className,
        )}
        {...getDataProps(rest)}
      >
        <span className="flex w-full flex-col items-start text-start overflow-hidden">
          {children ? (
            <span
              className={classNames(
                size === "sm" ? "text-sm/6" : "text-base",
                "w-full text-foreground truncate",
              )}
            >
              {children}
            </span>
          ) : (
            <span
              className={classNames(
                size === "sm" ? "text-sm/6" : "text-base",
                "w-full text-muted-foreground text-start truncate",
              )}
            >
              {placeholder}
            </span>
          )}
        </span>
        <ControlsChevronDown
          className={classNames(
            "text-muted-foreground flex-shrink-0 transition-transform",
            size === "sm" ? "text-base" : "text-2xl",
            isOpen && "rotate-180",
          )}
        />
      </button>
    </div>
  );
};

type HiddenInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value"
> & {
  value?: unknown;
};

const HiddenInput = ({ value: propValue, ...props }: HiddenInputProps) => {
  const { value: innerValue } = useContext(DropdownCtx);
  return (
    <input
      hidden
      value={(propValue ?? innerValue) as string}
      readOnly
      {...props}
    />
  );
};

const InsetSelect = Select;
const InsetMultiSelect = MultiSelect;

type HintProps = { children?: React.ReactNode; className?: string };

const Hint = ({ children, className }: HintProps) => {
  const { isError, disabled } = useContext(DropdownCtx);
  return (
    <p
      className={classNames(
        "text-xs mt-1",
        isError ? "text-destructive" : "text-muted-foreground",
        disabled && "opacity-60",
        className,
      )}
    >
      {children}
    </p>
  );
};

const Dropdown = Object.assign(DropdownRoot, {
  Options,
  Option,
  Trigger,
  Hint,
  Select,
  InsetSelect,
  MultiSelect,
  InsetMultiSelect,
  HiddenInput,
});

export default Dropdown;
